import { formatCount } from '@jmc/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { api, ApiRequestError } from '../lib/api.js';
import { useAuth } from '../lib/auth.js';
import { useI18n } from '../lib/i18n.js';
import {
  LocationPicker,
  LocationStatus,
  useLocationCodes,
  type LocationValue,
} from '../components/LocationPicker.js';
import {
  ConfirmDialog,
  cx,
  EmptyState,
  ErrorState,
  Field,
  InlineError,
  LoadingBlock,
  Modal,
  Mono,
  PageHeader,
  Panel,
  Select,
  Spinner,
  TextInput,
  Toast,
} from '../components/ui.js';

interface Bag {
  id: number;
  roomCode: string;
  rackCode: string;
  columnCode: string;
  bagCode: string;
  files: number;
  note: string | null;
}

interface TypoSuspect {
  level: 'rack' | 'column';
  roomCode: string;
  rackCode: string | null;
  codeA: string;
  codeB: string;
  filesA: number;
  filesB: number;
}

/**
 * Expands "C-01..C-06" into the six codes, and leaves anything else alone.
 *
 * Setting up a room means creating a few hundred shelves; typing them out one
 * by one is how people give up and let the entry form invent locations instead.
 */
export function expandCodes(input: string): string[] {
  const out: string[] = [];

  for (const piece of input.split(',').map((part) => part.trim()).filter(Boolean)) {
    const range = /^(.*?)(\d+)\.\.(?:\1)?(\d+)$/.exec(piece);
    if (!range) {
      out.push(piece);
      continue;
    }

    const [, prefix, startRaw, endRaw] = range;
    const start = Number(startRaw);
    const end = Number(endRaw);
    if (!Number.isFinite(start) || !Number.isFinite(end) || end < start || end - start > 500) {
      out.push(piece);
      continue;
    }

    const width = startRaw!.length;
    for (let value = start; value <= end; value += 1) {
      out.push(`${prefix ?? ''}${String(value).padStart(width, '0')}`);
    }
  }

  return [...new Set(out)];
}

export function LocationsPage() {
  const { t } = useI18n();
  const { can } = useAuth();
  const queryClient = useQueryClient();

  const [room, setRoom] = useState<string>('');
  const [rack, setRack] = useState<string>('');
  const [addOpen, setAddOpen] = useState(false);
  const [renameFor, setRenameFor] = useState<{
    level: 'room' | 'rack' | 'column';
    code: string;
    rackCode?: string | null;
  } | null>(null);
  const [moveFrom, setMoveFrom] = useState<Bag | null>(null);
  const [deleting, setDeleting] = useState<Bag | null>(null);
  const [toast, setToast] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const codes = useLocationCodes();
  const canManage = can('file:relocate');

  const rooms = codes.data?.rooms ?? [];
  const activeRoom = room || rooms[0] || '';
  const racks = codes.data?.racks[activeRoom] ?? [];

  const bags = useQuery({
    queryKey: ['location-bags', activeRoom, rack],
    queryFn: () =>
      api<{ bags: Bag[] }>(
        `/locations/bags?roomCode=${encodeURIComponent(activeRoom)}` +
          (rack ? `&rackCode=${encodeURIComponent(rack)}` : ''),
      ),
    enabled: Boolean(activeRoom),
  });

  const typos = useQuery({
    queryKey: ['location-typos'],
    queryFn: () => api<{ suspects: TypoSuspect[] }>('/locations/suspected-typos'),
    enabled: canManage,
  });

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['location-codes'] });
    void queryClient.invalidateQueries({ queryKey: ['location-bags'] });
    void queryClient.invalidateQueries({ queryKey: ['location-typos'] });
    void queryClient.invalidateQueries({ queryKey: ['rooms'] });
  };

  const removeBag = useMutation({
    mutationFn: (bag: Bag) => api(`/locations/bags/${bag.id}`, { method: 'DELETE' }),
    onSuccess: () => {
      setDeleting(null);
      setError(null);
      refresh();
    },
    onError: (caught) => {
      setDeleting(null);
      setError(caught instanceof ApiRequestError ? caught.message : t('common.error'));
    },
  });

  if (codes.isLoading) return <LoadingBlock />;
  if (codes.isError) {
    return <ErrorState error={codes.error} onRetry={() => void codes.refetch()} />;
  }

  return (
    <>
      <PageHeader
        title={t('location.title')}
        subtitle={t('location.subtitle')}
        actions={
          canManage ? (
            <button type="button" className="btn-primary" onClick={() => setAddOpen(true)}>
              {t('location.addAlmirah')}
            </button>
          ) : undefined
        }
      />

      {error && (
        <div className="mb-4">
          <InlineError message={error} />
        </div>
      )}

      {canManage && (typos.data?.suspects.length ?? 0) > 0 && (
        <Panel
          className="mb-4 border-amber-line"
          title={
            <span className="text-amber-ink">
              {t('location.typoReport')} ({typos.data!.suspects.length})
            </span>
          }
          bodyClassName="px-4 py-3"
        >
          <p className="mb-3 text-[0.8125rem] text-ink-500">{t('location.typoHint')}</p>
          <ul className="space-y-2">
            {typos.data!.suspects.map((suspect) => (
              <li
                key={`${suspect.level}-${suspect.roomCode}-${suspect.rackCode ?? ''}-${suspect.codeA}-${suspect.codeB}`}
                className="flex flex-wrap items-center gap-x-3 gap-y-1"
              >
                <span className="text-ink-500">
                  {t('field.room')} {suspect.roomCode}
                  {suspect.level === 'column' ? ` · ${t('field.rack')} ${suspect.rackCode}` : ''}:
                </span>
                <span className="badge border-line-strong bg-paper text-ink-500">
                  {suspect.level === 'column' ? t('field.column') : t('field.rack')}
                </span>
                <Mono className="font-medium">{suspect.codeA}</Mono>
                <span className="text-ink-400">({formatCount(suspect.filesA)})</span>
                <span className="text-ink-400">vs</span>
                <Mono className="font-medium">{suspect.codeB}</Mono>
                <span className="text-ink-400">({formatCount(suspect.filesB)})</span>
                <button
                  type="button"
                  className="btn-ghost px-2 py-0.5 text-[0.8125rem]"
                  onClick={() => {
                    setRoom(suspect.roomCode);
                    setRenameFor({
                      level: suspect.level,
                      rackCode: suspect.rackCode,
                      // Offer the emptier one as the thing to rename away.
                      code: suspect.filesA <= suspect.filesB ? suspect.codeA : suspect.codeB,
                    });
                  }}
                >
                  {t('location.rename')}
                </button>
              </li>
            ))}
          </ul>
        </Panel>
      )}

      <div className="mb-4 flex flex-wrap gap-3">
        <Field label={t('location.room')}>
          <Select
            value={activeRoom}
            onChange={(event) => {
              setRoom(event.target.value);
              setRack('');
            }}
          >
            {rooms.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </Select>
        </Field>

        <Field label={t('location.almirah')}>
          <Select value={rack} onChange={(event) => setRack(event.target.value)}>
            <option value="">{t('common.all')}</option>
            {racks.map((option) => (
              <option key={option} value={option}>
                {option}
              </option>
            ))}
          </Select>
        </Field>

        {canManage && rack && (
          <div className="flex items-end">
            <button
              type="button"
              className="btn-secondary"
              onClick={() => setRenameFor({ level: 'rack', code: rack })}
            >
              {t('location.rename')}
            </button>
          </div>
        )}
      </div>

      <Panel bodyClassName="overflow-x-auto">
        {bags.isLoading && <LoadingBlock />}
        {bags.data?.bags.length === 0 && <EmptyState title={t('common.none')} />}
        {bags.data && bags.data.bags.length > 0 && (
          <table className="data-table">
            <thead>
              <tr>
                <th>{t('field.rack')}</th>
                <th>{t('field.column')}</th>
                <th>{t('field.bag')}</th>
                <th className="text-right">{t('location.filesHere')}</th>
                {canManage && <th />}
              </tr>
            </thead>
            <tbody>
              {bags.data.bags.map((bag) => (
                <tr key={bag.id}>
                  <td><Mono>{bag.rackCode}</Mono></td>
                  <td><Mono>{bag.columnCode}</Mono></td>
                  <td><Mono>{bag.bagCode}</Mono></td>
                  <td className="text-right tabular">
                    {bag.files > 0 ? (
                      <Link
                        to={`/search?bagId=${bag.id}`}
                        className="text-navy-600 hover:underline"
                      >
                        {formatCount(bag.files)}
                      </Link>
                    ) : (
                      <span className="text-ink-400">{t('location.emptyShelf')}</span>
                    )}
                  </td>
                  {canManage && (
                    <td className="whitespace-nowrap text-right">
                      {bag.files > 0 ? (
                        <button
                          type="button"
                          className="btn-ghost px-2 py-1"
                          onClick={() => setMoveFrom(bag)}
                        >
                          {t('location.moveContents')}
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="btn-ghost px-2 py-1"
                          onClick={() => setDeleting(bag)}
                        >
                          {t('location.deleteBag')}
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Panel>

      {addOpen && (
        <AddAlmirahDialog
          rooms={rooms}
          defaultRoom={activeRoom}
          onClose={() => setAddOpen(false)}
          onDone={(created) => {
            setAddOpen(false);
            setToast(t('location.created', { n: created }));
            refresh();
          }}
        />
      )}

      {renameFor && (
        <RenameDialog
          level={renameFor.level}
          roomCode={activeRoom}
          rackCode={renameFor.rackCode ?? undefined}
          from={renameFor.code}
          onClose={() => setRenameFor(null)}
          onDone={(files) => {
            setRenameFor(null);
            setRack('');
            setToast(t('location.renamed', { files }));
            refresh();
          }}
        />
      )}

      {moveFrom && (
        <MoveContentsDialog
          from={moveFrom}
          onClose={() => setMoveFrom(null)}
          onDone={(moved) => {
            setMoveFrom(null);
            setToast(t('location.moved', { n: moved }));
            refresh();
          }}
        />
      )}

      <ConfirmDialog
        open={deleting !== null}
        title={t('confirm.title')}
        message={
          deleting
            ? `${t('location.deleteBag')}: ${deleting.rackCode} / ${deleting.columnCode} / ${deleting.bagCode}`
            : ''
        }
        confirmLabel={t('confirm.proceed')}
        busy={removeBag.isPending}
        onConfirm={() => deleting && removeBag.mutate(deleting)}
        onCancel={() => setDeleting(null)}
      />

      {toast && <Toast message={toast} onDismiss={() => setToast(null)} />}
    </>
  );
}

function AddAlmirahDialog({
  rooms,
  defaultRoom,
  onClose,
  onDone,
}: {
  rooms: string[];
  defaultRoom: string;
  onClose: () => void;
  onDone: (created: number) => void;
}) {
  const { t } = useI18n();
  const [roomCode, setRoomCode] = useState(defaultRoom);
  const [rackCode, setRackCode] = useState('');
  const [columnsText, setColumnsText] = useState('C-01..C-06');
  const [bagsText, setBagsText] = useState('B-01..B-10');
  const [error, setError] = useState<string | null>(null);

  const columnCodes = expandCodes(columnsText);
  const bagCodes = expandCodes(bagsText);
  const total = columnCodes.length * bagCodes.length;

  const create = useMutation({
    mutationFn: () =>
      api<{ created: number; existing: number }>('/locations/bags', {
        method: 'POST',
        body: { roomCode, rackCode, columnCodes, bagCodes },
      }),
    onSuccess: (result) => onDone(result.created),
    onError: (caught) =>
      setError(caught instanceof ApiRequestError ? caught.message : t('common.error')),
  });

  return (
    <Modal
      open
      title={t('location.addAlmirah')}
      onClose={onClose}
      width="max-w-xl"
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            {t('action.cancel')}
          </button>
          <button
            type="submit"
            form="add-almirah"
            className="btn-primary"
            disabled={create.isPending || !roomCode.trim() || !rackCode.trim() || total === 0}
          >
            {create.isPending && <Spinner />}
            {t('action.save')}
          </button>
        </>
      }
    >
      {error && <div className="mb-4"><InlineError message={error} /></div>}

      <form
        id="add-almirah"
        className="grid gap-4 sm:grid-cols-2"
        onSubmit={(event: FormEvent) => {
          event.preventDefault();
          setError(null);
          create.mutate();
        }}
      >
        <Field label={t('location.room')} required>
          <TextInput
            value={roomCode}
            onChange={(event) => setRoomCode(event.target.value)}
            list="known-rooms"
            required
          />
          <datalist id="known-rooms">
            {rooms.map((option) => (
              <option key={option} value={option} />
            ))}
          </datalist>
        </Field>

        <Field label={t('location.almirah')} required>
          <TextInput
            value={rackCode}
            onChange={(event) => setRackCode(event.target.value)}
            placeholder="R-13"
            required
          />
        </Field>

        <Field label={t('location.columnsLabel')} hint={t('location.rangeHint')} required>
          <TextInput value={columnsText} onChange={(event) => setColumnsText(event.target.value)} />
        </Field>

        <Field label={t('location.bagsLabel')} hint={t('location.rangeHint')} required>
          <TextInput value={bagsText} onChange={(event) => setBagsText(event.target.value)} />
        </Field>

        <div className="sm:col-span-2 rounded-[5px] bg-paper px-3 py-2 text-[0.8125rem]">
          {formatCount(columnCodes.length)} × {formatCount(bagCodes.length)} ={' '}
          <span className="font-medium">{formatCount(total)}</span> shelves
          {total > 0 && (
            <span className="ml-2 text-ink-500">
              ({columnCodes[0]}/{bagCodes[0]} … {columnCodes.at(-1)}/{bagCodes.at(-1)})
            </span>
          )}
        </div>
      </form>
    </Modal>
  );
}

function RenameDialog({
  level,
  roomCode,
  rackCode,
  from,
  onClose,
  onDone,
}: {
  level: 'room' | 'rack' | 'column';
  roomCode: string;
  rackCode?: string | undefined;
  from: string;
  onClose: () => void;
  onDone: (filesAffected: number) => void;
}) {
  const { t } = useI18n();
  const [to, setTo] = useState(from);
  const [error, setError] = useState<string | null>(null);

  const rename = useMutation({
    mutationFn: () =>
      api<{ bagsMoved: number; filesAffected: number; merged: number }>('/locations/rename', {
        method: 'POST',
        body: { level, roomCode, rackCode, from, to },
      }),
    onSuccess: (result) => onDone(result.filesAffected),
    onError: (caught) =>
      setError(caught instanceof ApiRequestError ? caught.message : t('common.error')),
  });

  return (
    <Modal
      open
      title={t('location.renameTitle')}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            {t('action.cancel')}
          </button>
          <button
            type="submit"
            form="rename-code"
            className="btn-primary"
            disabled={rename.isPending || to.trim() === '' || to.trim() === from}
          >
            {rename.isPending && <Spinner />}
            {t('location.rename')}
          </button>
        </>
      }
    >
      {error && <div className="mb-4"><InlineError message={error} /></div>}
      <p className="mb-4 text-[0.8125rem] text-ink-500">{t('location.renameHint')}</p>

      <form
        id="rename-code"
        className="space-y-4"
        onSubmit={(event: FormEvent) => {
          event.preventDefault();
          setError(null);
          rename.mutate();
        }}
      >
        <Field label={t('location.from')}>
          <TextInput value={from} disabled />
        </Field>
        <Field label={t('location.to')} required>
          <TextInput value={to} onChange={(event) => setTo(event.target.value)} autoFocus required />
        </Field>
      </form>
    </Modal>
  );
}

function MoveContentsDialog({
  from,
  onClose,
  onDone,
}: {
  from: Bag;
  onClose: () => void;
  onDone: (moved: number) => void;
}) {
  const { t } = useI18n();
  // The destination used to be a dropdown of the bags in the room already on
  // screen, which quietly made the one case a reorganisation usually is --
  // "this almirah is being emptied into the new room" -- impossible. Four
  // linked pickers reach any shelf, and may name one that does not exist yet.
  const [to, setTo] = useState<LocationValue>({
    roomCode: from.roomCode,
    rackCode: '',
    columnCode: '',
    bagCode: '',
  });
  const [removeEmptied, setRemoveEmptied] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const complete =
    to.roomCode.trim() && to.rackCode.trim() && to.columnCode.trim() && to.bagCode.trim();

  const move = useMutation({
    mutationFn: () =>
      api<{ moved: number }>('/locations/move', {
        method: 'POST',
        body: {
          fromBagId: from.id,
          to: {
            roomCode: to.roomCode.trim(),
            rackCode: to.rackCode.trim(),
            columnCode: to.columnCode.trim(),
            bagCode: to.bagCode.trim(),
          },
          removeEmptied,
        },
      }),
    onSuccess: (result) => onDone(result.moved),
    onError: (caught) =>
      setError(caught instanceof ApiRequestError ? caught.message : t('common.error')),
  });

  return (
    <Modal
      open
      title={t('location.moveContents')}
      onClose={onClose}
      footer={
        <>
          <button type="button" className="btn-secondary" onClick={onClose}>
            {t('action.cancel')}
          </button>
          <button
            type="submit"
            form="move-contents"
            className="btn-primary"
            disabled={move.isPending || !complete}
          >
            {move.isPending && <Spinner />}
            {t('location.moveContents')}
          </button>
        </>
      }
    >
      {error && <div className="mb-4"><InlineError message={error} /></div>}

      <form
        id="move-contents"
        className="space-y-4"
        onSubmit={(event: FormEvent) => {
          event.preventDefault();
          setError(null);
          move.mutate();
        }}
      >
        <p className="rounded-[5px] bg-paper px-3 py-2 text-[0.8125rem]">
          {t('location.from')}:{' '}
          <Mono className="font-medium">
            {from.roomCode} / {from.rackCode} / {from.columnCode} / {from.bagCode}
          </Mono>{' '}
          — {formatCount(from.files)} {t('location.filesHere')}
        </p>

        <div className="grid gap-3 sm:grid-cols-2">
          <LocationPicker value={to} onChange={setTo} disabled={move.isPending} />
        </div>
        <LocationStatus value={to} />

        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={removeEmptied}
            onChange={(event) => setRemoveEmptied(event.target.checked)}
            className="h-4 w-4 rounded border-line-strong"
          />
          <span className={cx('text-[0.8125rem]')}>{t('location.deleteBag')}</span>
        </label>
      </form>
    </Modal>
  );
}
